import { logger } from './logger';

// Lightweight procedural SFX engine built directly on the Web Audio API.
// Sounds are synthesized at runtime (oscillators + noise bursts) rather than
// loaded from audio files, so there are no external assets to fetch, license,
// or attribute, and the bundle carries zero extra bytes.

export type SfxName =
  | 'hit'
  | 'ranged'
  | 'crit'
  | 'miss'
  | 'skillCast'
  | 'death'
  | 'victory'
  | 'defeat'
  | 'achievement'
  | 'levelUp'
  | 'uiClick';

const STORAGE_KEY = 'hg_sound_settings';

interface SoundSettings {
  volume: number; // 0..1
  muted: boolean;
}

function loadSettings(): SoundSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        volume: typeof parsed.volume === 'number' ? Math.min(1, Math.max(0, parsed.volume)) : 0.5,
        muted: !!parsed.muted,
      };
    }
  } catch (err) {
    logger.warn('Failed to load sound settings:', err);
  }
  return { volume: 0.5, muted: false };
}

let settings = loadSettings();

function saveSettings() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (err) {
    logger.warn('Failed to save sound settings:', err);
  }
}

export function getVolume(): number {
  return settings.volume;
}

export function setVolume(volume: number): void {
  settings = { ...settings, volume: Math.min(1, Math.max(0, volume)) };
  saveSettings();
}

export function isMuted(): boolean {
  return settings.muted;
}

export function setMuted(muted: boolean): void {
  settings = { ...settings, muted };
  saveSettings();
}

let audioCtx: AudioContext | null = null;

function getContext(): AudioContext | null {
  try {
    if (!audioCtx) {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      audioCtx = new Ctor();
    }
    return audioCtx;
  } catch (err) {
    logger.warn('Web Audio unavailable:', err);
    return null;
  }
}

// Browsers suspend new AudioContexts until a user gesture; resume on the first
// click/keydown/touch so sounds are ready by the time gameplay starts.
function resumeOnGesture() {
  const ctx = getContext();
  if (ctx && ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
}
if (typeof window !== 'undefined') {
  ['pointerdown', 'keydown', 'touchstart'].forEach(evt => {
    window.addEventListener(evt, resumeOnGesture, { passive: true });
  });
}

let noiseBuffer: AudioBuffer | null = null;
function getNoiseBuffer(ctx: AudioContext): AudioBuffer {
  if (noiseBuffer) return noiseBuffer;
  const buffer = ctx.createBuffer(1, ctx.sampleRate * 0.5, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  noiseBuffer = buffer;
  return buffer;
}

function playTone(
  ctx: AudioContext,
  master: GainNode,
  startTime: number,
  freqStart: number,
  freqEnd: number,
  duration: number,
  type: OscillatorType,
  peakGain: number
) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freqStart, startTime);
  if (freqEnd !== freqStart) osc.frequency.exponentialRampToValueAtTime(Math.max(1, freqEnd), startTime + duration);
  gain.gain.setValueAtTime(0.0001, startTime);
  gain.gain.exponentialRampToValueAtTime(peakGain, startTime + Math.min(0.02, duration / 4));
  gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
  osc.connect(gain);
  gain.connect(master);
  osc.start(startTime);
  osc.stop(startTime + duration + 0.02);
}

function playNoiseBurst(
  ctx: AudioContext,
  master: GainNode,
  startTime: number,
  duration: number,
  filterFreq: number,
  peakGain: number
) {
  const src = ctx.createBufferSource();
  src.buffer = getNoiseBuffer(ctx);
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = filterFreq;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, startTime);
  gain.gain.exponentialRampToValueAtTime(peakGain, startTime + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
  src.connect(filter);
  filter.connect(gain);
  gain.connect(master);
  src.start(startTime);
  src.stop(startTime + duration + 0.02);
}

type SfxBuilder = (ctx: AudioContext, master: GainNode, t0: number) => void;

const builders: Record<SfxName, SfxBuilder> = {
  hit: (ctx, master, t0) => {
    playNoiseBurst(ctx, master, t0, 0.08, 1200, 0.9);
    playTone(ctx, master, t0, 140, 80, 0.12, 'sine', 0.6);
  },
  ranged: (ctx, master, t0) => {
    playTone(ctx, master, t0, 1400, 300, 0.15, 'sawtooth', 0.25);
    playNoiseBurst(ctx, master, t0 + 0.1, 0.05, 2000, 0.4);
  },
  crit: (ctx, master, t0) => {
    playNoiseBurst(ctx, master, t0, 0.1, 1600, 1);
    playTone(ctx, master, t0, 180, 60, 0.18, 'sawtooth', 0.7);
    playTone(ctx, master, t0 + 0.06, 900, 700, 0.12, 'square', 0.3);
  },
  miss: (ctx, master, t0) => {
    playTone(ctx, master, t0, 500, 900, 0.15, 'sine', 0.2);
  },
  skillCast: (ctx, master, t0) => {
    playTone(ctx, master, t0, 440, 440, 0.1, 'sine', 0.25);
    playTone(ctx, master, t0 + 0.08, 660, 660, 0.1, 'sine', 0.25);
    playTone(ctx, master, t0 + 0.16, 880, 1100, 0.2, 'sine', 0.3);
  },
  death: (ctx, master, t0) => {
    playTone(ctx, master, t0, 300, 60, 0.4, 'sawtooth', 0.35);
    playNoiseBurst(ctx, master, t0, 0.15, 400, 0.3);
  },
  victory: (ctx, master, t0) => {
    [523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => {
      playTone(ctx, master, t0 + i * 0.14, freq, freq, 0.3, 'triangle', 0.3);
    });
  },
  defeat: (ctx, master, t0) => {
    [392, 349.23, 293.66, 220].forEach((freq, i) => {
      playTone(ctx, master, t0 + i * 0.22, freq, freq * 0.9, 0.4, 'sawtooth', 0.25);
    });
  },
  achievement: (ctx, master, t0) => {
    playTone(ctx, master, t0, 880, 880, 0.12, 'sine', 0.25);
    playTone(ctx, master, t0 + 0.1, 1318.5, 1318.5, 0.35, 'sine', 0.28);
  },
  levelUp: (ctx, master, t0) => {
    // Quick triumphant ascending run (same C5-E5-G5-C6 progression as victory, but
    // faster) plus a sparkling top note - distinct from both achievement and victory.
    [523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => {
      playTone(ctx, master, t0 + i * 0.09, freq, freq * 1.05, 0.18, 'triangle', 0.3);
    });
    playTone(ctx, master, t0 + 0.36, 1567.98, 1567.98, 0.3, 'sine', 0.25);
  },
  uiClick: (ctx, master, t0) => {
    playTone(ctx, master, t0, 1600, 1600, 0.03, 'square', 0.15);
  },
};

// Never throws: a missing AudioContext, a locked autoplay policy, or any
// synthesis error should silently no-op rather than break gameplay.
export function playSfx(name: SfxName): void {
  try {
    if (settings.muted || settings.volume <= 0) return;
    const ctx = getContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    const master = ctx.createGain();
    master.gain.value = settings.volume;
    master.connect(ctx.destination);
    const builder = builders[name];
    if (builder) builder(ctx, master, ctx.currentTime);
  } catch (err) {
    logger.warn(`Failed to play SFX "${name}":`, err);
  }
}
